import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SCAN_SENSITIVITIES, type ScanDetector, type ScanSensitivity } from '../../../types/climb-scan';

interface RescanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What is being looked at again: the whole wall, or one of its colours. */
  scope: 'scan' | 'color';
  /**
   * Which detector answers unless the setter picks the precise look, and
   * therefore **which question is worth asking**. See the comment on the
   * component.
   */
  detector: ScanDetector;
  /**
   * Whether SAM 3 can be picked instead of `detector`: the precise look,
   * slower and paid for (`ClimbScan.precise_scan`). Only for the whole wall.
   */
  precise?: boolean;
  /** Whether the precise look starts picked: the wall's answer was SAM 3's. */
  preciseByDefault?: boolean;
  /**
   * Whether this wall has ever answered. A first look has no last answer to ask
   * about — nothing was wrong with it, there was nothing — so the words change
   * even though the press, the endpoint and the parameters do not.
   */
  answered: boolean;
  /**
   * `wholePhoto` is only ever true on a re-scan the segmentation model reads —
   * see the comment on the component. `chosen` is `'sam'` when the precise
   * look was picked, null for the default.
   */
  onConfirm: (
    reason: string | null,
    sensitivity: ScanSensitivity | null,
    wholePhoto: boolean,
    chosen: ScanDetector | null
  ) => Promise<void> | void;
  running: boolean;
}

/**
 * The ways an analysis usually goes wrong, offered as one press each.
 *
 * They exist because the reason is the *only* thing the model is told about the
 * last answer, and a reason nobody can be bothered to type is no reason at all.
 * Each is a directed correction — "you merged two colours" tells the model what
 * to do differently, which showing it its own wrong boxes does not.
 *
 * **Gemini only.** See below.
 */
const PRESETS = ['mergedColors', 'missedHolds', 'extraHolds', 'misalignedBoxes'] as const;

/**
 * Asks what should be different about the second look, then starts it.
 *
 * **Every press of Scan comes through here**, the first look included. It costs
 * a model call and a cold boot either way, and the two things worth settling
 * before one — why the last answer was wrong, or how hard to look this time —
 * are the whole of what this collects; a press that started the analysis on its
 * own left them unreachable. A wall nobody has looked at yet is asked the same
 * question in words that fit: there is no last answer to criticise, so the
 * presets and the "what was wrong" framing go, and the parameters stay.
 *
 * **The question depends on who is answering, and that is not a detail.** A
 * language model's answer depends on the words it is given, so Gemini is asked
 * *why* the last one was wrong and told. A segmentation model's does not: SAM
 * run twice on the same pixels with the same parameters returns the same
 * answer, byte for byte, so "it merged two colours" is not something it can act
 * on. What it can act on is how hard to look — the confidence it reports at and
 * how small a thing may be before we call it chalk — so that is what this asks
 * for instead.
 *
 * **The look at the whole photo is a re-scan's to ask for.** The photo is read
 * in pieces, and a big hold or a volume wider than the overlap between two
 * pieces can come back cut or not at all; one more call, on the whole photo
 * shrunk to the size of a piece, sees those whole. It is offered only where the
 * segmentation model reads the wall and the wall has answered before: the
 * first look is what shows whether a wall needs it, and every press of it is
 * paid for. The server ignores it on a first look anyway.
 *
 * **On a gym with both segmentation models the setter picks who answers.**
 * The local model is the default — free, and done in a minute — and SAM 3 is
 * offered as the precise look: it finds more of the setters' holds in their
 * colour, at a price and with a cold boot. It starts picked on a wall SAM 3
 * answered, so pressing on asks the same model again.
 */
export function RescanDialog({
  open,
  onOpenChange,
  scope,
  detector,
  precise = false,
  preciseByDefault = false,
  answered,
  onConfirm,
  running,
}: RescanDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [sensitivity, setSensitivity] = useState<ScanSensitivity>('normal');
  const [wholePhoto, setWholePhoto] = useState(false);
  // Null until the setter touches the switch: until then it follows the wall.
  const [preciseChoice, setPreciseChoice] = useState<boolean | null>(null);
  const offersPrecise = precise && scope === 'scan';
  const preciseOn = offersPrecise && (preciseChoice ?? preciseByDefault);
  const answering: ScanDetector = preciseOn ? 'sam' : detector;
  // Both segmentation models hear parameters, not words.
  const measured = answering !== 'gemini';
  const first = !answered;
  // Only SAM 3 waits to be asked: the local model looks at the whole photo
  // on every wall, since it costs it a third of a second.
  const offersWholePhoto = answering === 'sam' && !first;

  // The switch follows the next wall opened, not the last one touched.
  function handleOpenChange(next: boolean) {
    if (!next) setPreciseChoice(null);
    onOpenChange(next);
  }

  function togglePreset(preset: string) {
    const label = t(`gymAdmin.scans.rescan.presets.${preset}`);
    setReason((current) => {
      if (current.includes(label)) return current.replace(label, '').replace(/\s{2,}/g, ' ').trim();

      return current ? `${current} ${label}` : label;
    });
  }

  async function handleConfirm() {
    await onConfirm(
      measured ? null : reason.trim() || null,
      measured ? sensitivity : null,
      offersWholePhoto && wholePhoto,
      preciseOn ? 'sam' : null
    );
    setReason('');
    setSensitivity('normal');
    setWholePhoto(false);
    setPreciseChoice(null);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(first ? 'gymAdmin.scans.rescan.title.first' : `gymAdmin.scans.rescan.title.${scope}`)}
          </DialogTitle>
          <DialogDescription>
            {measured
              ? t(
                  first
                    ? 'gymAdmin.scans.rescan.sensitivity.hintFirst'
                    : 'gymAdmin.scans.rescan.sensitivity.hint'
                )
              : t(first ? 'gymAdmin.scans.rescan.hint.first' : `gymAdmin.scans.rescan.hint.${scope}`)}
          </DialogDescription>
          {/* Who answers is not obvious any more: a gym can have the free local
              model and SAM 3 side by side, and only one of them is paid for
              and slow to wake up. */}
          <p data-testid="rescan-answered-by" className="text-xs font-medium text-muted-foreground">
            {t(`gymAdmin.scans.rescan.answeredBy.${answering}`)}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {offersPrecise && (
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <Label htmlFor="rescan-precise">{t('gymAdmin.scans.rescan.precise.label')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('gymAdmin.scans.rescan.precise.explain')}
                </p>
              </div>
              <Switch id="rescan-precise" checked={preciseOn} onCheckedChange={setPreciseChoice} />
            </div>
          )}
          {measured ? (
            <div className="space-y-2">
              <Label id="rescan-sensitivity-label">
                {t('gymAdmin.scans.rescan.sensitivity.label')}
              </Label>
              <div
                role="radiogroup"
                aria-labelledby="rescan-sensitivity-label"
                className="flex flex-wrap gap-2"
              >
                {SCAN_SENSITIVITIES.map((level) => {
                  const on = sensitivity === level;

                  return (
                    <button
                      key={level}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setSensitivity(level)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background hover:bg-muted'
                      }`}
                    >
                      {t(`gymAdmin.scans.rescan.sensitivity.levels.${level}`)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {t(`gymAdmin.scans.rescan.sensitivity.explain.${sensitivity}`)}
              </p>
              {offersWholePhoto && (
                <div className="flex items-start justify-between gap-3 pt-2">
                  <div className="space-y-1">
                    <Label htmlFor="rescan-whole-photo">
                      {t('gymAdmin.scans.rescan.wholePhoto.label')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('gymAdmin.scans.rescan.wholePhoto.explain')}
                    </p>
                  </div>
                  <Switch
                    id="rescan-whole-photo"
                    checked={wholePhoto}
                    onCheckedChange={setWholePhoto}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="rescan-reason">
                {t(first ? 'gymAdmin.scans.rescan.reasonFirst' : 'gymAdmin.scans.rescan.reason')}
              </Label>
              {/* Every one of them names something the last answer got wrong,
                  so a wall that has not answered is offered none. */}
              {!first && (
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((preset) => {
                    const label = t(`gymAdmin.scans.rescan.presets.${preset}`);
                    const on = reason.includes(label);

                    return (
                      <button
                        key={preset}
                        type="button"
                        aria-pressed={on}
                        onClick={() => togglePreset(preset)}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                          on
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              <Textarea
                id="rescan-reason"
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t(
                  first
                    ? 'gymAdmin.scans.rescan.reasonFirstPlaceholder'
                    : 'gymAdmin.scans.rescan.reasonPlaceholder'
                )}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={running}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={running}>
            {running ? (
              <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="mr-2 size-4" aria-hidden />
            )}
            {t(first ? 'gymAdmin.scans.rescan.startFirst' : 'gymAdmin.scans.rescan.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
